import type { AggParentCandidate } from '@atlassian/jira-issue-field-parent-editview-full/src/common/types.tsx';
import type { NullableParentOption } from '@atlassian/jira-issue-field-parent-editview-full/src/ui/parent/types.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import type { AggJiraParent } from './types.tsx';

export const transformOptionToAgg = (option: NullableParentOption): AggJiraParent | null => {
	if (!option) {
		return null;
	}
	return {
		id: String(option?.id) || '',
		issueId: option?.issueId || '',
		webUrl: option?.webUrl || '',
		key: option?.key,
		issueColorField: {
			color: {
				colorKey: option?.color || '',
			},
		},
		issueTypeField: {
			issueType: {
				avatar: {
					xsmall: option?.fields?.issuetype?.iconUrl || '',
				},
				name: option?.fields?.issuetype?.name || '',
			},
		},
		summaryField: {
			text: option?.fields.summary || '',
		},
	};
};

type ParentIssue = AggParentCandidate & {
	summaryField: {
		id: string;
		text?: string;
	};
	issueTypeField: {
		id: string;
		issueType: {
			id: string;
			avatar: {
				xsmall: string;
				medium?: string;
			};
			name: string;
		};
	};
	statusField?: {
		// Make required when removing dfo_remove_fields_by_id
		id: string;
		status: {
			statusCategory: {
				statusCategoryId: string;
			};
		};
	};
};

export type ParentFieldOptimisticResponse = {
	id: string;
	fieldId: string;
	type: string;
	name: string;
	parentIssue: ParentIssue | null;
};

export const transformOptionToOptimisticFieldResponse = (
	field: {
		id: string;
		fieldId: string;
		type: string;
		name: string;
	},
	aggValue: AggParentCandidate,
): ParentFieldOptimisticResponse => {
	if (fg('dfo_remove_fields_by_id')) {
		const summaryField = aggValue?.summaryField;
		const issueTypeField = aggValue?.issueTypeField;
		const statusField = aggValue?.statusField;

		return {
			id: field?.id,
			fieldId: field?.fieldId,
			type: field?.type,
			name: field?.name,
			parentIssue: aggValue
				? {
						id: aggValue?.id,
						issueId: aggValue?.issueId,
						webUrl: aggValue?.webUrl,
						key: aggValue?.key,
						issueColorField: aggValue?.issueColorField,
						summaryField: {
							id: summaryField?.id || '',
							fieldId: summaryField?.fieldId || '',
							text: summaryField?.text || '',
						},
						issueTypeField: {
							id: issueTypeField?.id || '',
							fieldId: issueTypeField?.fieldId || '',
							issueType: {
								id: issueTypeField?.issueType?.id || '',
								avatar: {
									xsmall: issueTypeField?.issueType?.avatar?.xsmall || '',
								},
								hierarchy: {
									level: issueTypeField?.issueType?.hierarchy?.level || 0,
								},
								issueTypeId: issueTypeField?.issueType?.issueTypeId || '',
								name: issueTypeField?.issueType?.name || '',
							},
						},
						statusField: {
							id: statusField?.id || '',
							fieldId: statusField?.fieldId || '',
							status: {
								id: statusField?.status?.id || '',
								statusCategory: {
									id: statusField?.status?.statusCategory?.id || '',
									statusCategoryId: statusField?.status?.statusCategory?.statusCategoryId || '',
								},
							},
						},
					}
				: null,
		};
	}

	const summaryField = aggValue?.fieldsById?.edges?.find(
		(edge) => edge?.node?.__typename === 'JiraSingleLineTextField',
	);
	const issueTypeField = aggValue?.fieldsById?.edges?.find(
		(edge) => edge?.node?.__typename === 'JiraIssueTypeField',
	);
	return {
		id: field?.id,
		fieldId: field?.fieldId,
		type: field?.type,
		name: field?.name,
		parentIssue: aggValue
			? {
					id: aggValue?.id,
					issueId: aggValue?.issueId,
					webUrl: aggValue?.webUrl,
					key: aggValue?.key,
					issueColorField: aggValue?.issueColorField,
					fieldsById: aggValue?.fieldsById,

					summaryField: {
						id: summaryField?.node?.id || '',
						text: summaryField?.node?.text || '',
						fieldId: summaryField?.node?.fieldId || '', // Added to keep types in sync with new code path
					},
					issueTypeField: {
						id: issueTypeField?.node?.id || '',
						fieldId: issueTypeField?.node?.fieldId || '', // Added to keep types in sync with new code path
						issueType: {
							id: issueTypeField?.node?.issueType?.id || '',
							avatar: {
								xsmall: issueTypeField?.node?.issueType?.avatar?.xsmall || '',
							},
							// Added to keep types in sync with new code path
							hierarchy: {
								level: issueTypeField?.node?.issueType?.hierarchy?.level || 0,
							},
							issueTypeId: issueTypeField?.node?.issueType?.issueTypeId || '', // Added to keep types in sync with new code path
							name: issueTypeField?.node?.issueType?.name || '',
						},
					},
				}
			: null,
	};
};
